import * as React from "react";
import { cn } from "../utils/cn";
import { Button } from "./button";
import { SearchInput } from "./search-input";
import { Text } from "../primitives/typography";

/**
 * The shell every filtered list wants: a search box, whatever selects that list needs, a Clear that
 * only appears once something is narrowed, and a count of what survived.
 *
 * **The selects are `children`, not props.** Six lists use this and no two narrow by the same things
 * — role/status/organization, provider/connection status, category, license. A prop per filter would
 * mean a component that knows about every domain on the platform; composition keeps each page's own
 * pickers in the page and shares only the parts that are actually identical.
 *
 * Written after hand-rolling the same flex row six times in one sitting, which is what the second-use
 * rule in `.claude/rules/architecture.md` is there to catch.
 */
export interface FilterBarProps extends Omit<React.ComponentProps<"div">, "children"> {
    search: string;
    onSearchChange: (value: string) => void;
    /** Accessible name for the search box — "Search users", "Search organizations". */
    searchLabel: string;
    searchPlaceholder?: string;
    /** Whether anything is currently narrowed. Drives Clear and the count. */
    active: boolean;
    onClear: () => void;
    /** How many rows survived, out of how many there are. Omit where the total is not knowable. */
    count?: { shown: number; total: number };
    /** Said only while narrowed — e.g. that a hierarchy has flattened into a list of matches. */
    note?: React.ReactNode;
    /** The list's own pickers. */
    children?: React.ReactNode;
    searchClassName?: string;
}

const FilterBar = ({
    search,
    onSearchChange,
    searchLabel,
    searchPlaceholder,
    active,
    onClear,
    count,
    note,
    children,
    className,
    searchClassName,
    ...props
}: FilterBarProps) => (
    <div className={cn("mb-4 flex flex-wrap items-center gap-2", className)} {...props}>
        <SearchInput
            label={searchLabel}
            placeholder={searchPlaceholder}
            className={cn("max-w-sm", searchClassName)}
            value={search}
            onValueChange={onSearchChange}
        />
        {children}
        {/* Both appear only while narrowed: a permanent Clear implies there is something to clear,
            and "12 of 12" is noise. */}
        {active && (
            <>
                <Button variant="ghost" size="sm" className="w-auto" onClick={onClear}>
                    Clear
                </Button>
                {count && (
                    <Text muted className="text-xs">
                        {count.shown.toLocaleString()} of {count.total.toLocaleString()}
                    </Text>
                )}
                {note && (
                    <Text muted className="text-xs">
                        {note}
                    </Text>
                )}
            </>
        )}
    </div>
);

export { FilterBar };
